import {
  Component,
  OnInit,
  ViewEncapsulation,
  inject,
  Renderer2,
  ElementRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

interface TodoItem {
  task: string;
  completed: boolean;
}

@Component({
  selector: 'app-settingsPanel',
  templateUrl: './settings-panel.component.html',
  styleUrl: './settings-panel.component.scss',
})
export class SettingsPanelComponent implements OnInit {
  private fb = inject(FormBuilder);
  public isDefaultLayout: boolean | undefined;
  public isBoxedLayout: boolean | undefined;
  public isSidebarIconOnly: boolean | undefined;
  public isCompactMenu: boolean | undefined;
  public isFixedSidebar: boolean | undefined;
  public isHiddenSidebar: boolean | undefined;
  public isOverlaySidebar: boolean | undefined;
  active = 1;

  constructor(private renderer: Renderer2, private el: ElementRef) {
    this.form = this.fb.group({
      todoitem: ['', [Validators.required, Validators.minLength(1)]],
    });
  }

  form: FormGroup;
  todoArray: TodoItem[] = [
    {task: 'Meeting with Urban Team', completed: false},
    {task: 'Duplicate a project for new customer', completed: false},
    {task: 'Project meeting with CEO', completed: false},
    {task: 'Follow up of team zilla', completed: false},
    {task: 'Level up for Antony', completed: false},
  ];

  addTodo(): void {
    if (this.form.valid && this.form.value.todoitem?.trim()) {
      const newTodoItem: TodoItem = {
        task: this.form.value.todoitem.trim(),
        completed: false,
      };
      this.todoArray.push(newTodoItem);
      this.form.reset();
    }
  }

  removeTodoItem(item: TodoItem): void {
    const index = this.todoArray.indexOf(item);
    if (index > -1) {
      this.todoArray.splice(index, 1);
    }
  }

  changeTodoStatus(event: Event, index: number): void {
    const target = event.target as HTMLInputElement;
    this.todoArray[index].completed = target.checked;
  }

  // Alternative method using index directly
  removeTodoByIndex(index: number): void {
    if (index >= 0 && index < this.todoArray.length) {
      this.todoArray.splice(index, 1);
    }
  }

  // Getter for form control (useful for template validation)
  get todoitemControl() {
    return this.form.get('todoitem');
  }

  // Track by function for better performance in *ngFor
  trackByIndex(index: number, item: TodoItem): number {
    return index;
  }

  ngOnInit() {
    const body = document.querySelector('body');
    if (!body) return;

    switch (true) {
      case body.classList.contains('boxed-layout'):
        this.isBoxedLayout = true;
        break;
      case body.classList.contains('sidebar-icon-only'):
        this.isSidebarIconOnly = true;
        break;
      case body.classList.contains('sidebar-mini'):
        this.isCompactMenu = true;
        break;
      case body.classList.contains('sidebar-fixed'):
        this.isFixedSidebar = true;
        break;
      case body.classList.contains('sidebar-toggle-display'):
        this.isHiddenSidebar = true;
        break;
      case body.classList.contains('sidebar-absolute'):
        this.isOverlaySidebar = true;
        break;
      default:
        this.isDefaultLayout = true;
    }
  }

  toggleThemeSettings() {
    const themeSettings = document.querySelector('#theme-settings');
    if (themeSettings) {
      this.renderer.addClass(themeSettings, 'open');
    }
  }

  closeSettingsSidebar(el: string) {
    if (el === 'theme') {
      const themeSettings = document.querySelector('#theme-settings');
      if (themeSettings) {
        this.renderer.removeClass(themeSettings, 'open');
      }
    } else {
      const rightSidebar = document.querySelector('#right-sidebar');
      if (rightSidebar) {
        this.renderer.removeClass(rightSidebar, 'open'); // Changed from addClass
      }
    }
  }

  changeSidebarTheme(theme: string) {
    const body = document.querySelector('body');
    if (!body) return;

    this.renderer.removeClass(body, 'sidebar-light');
    this.renderer.removeClass(body, 'sidebar-dark');

    document.querySelectorAll('.sidebar-bg-options').forEach((el) => {
      this.renderer.removeClass(el, 'selected');
    });

    if (theme === 'light') {
      this.renderer.addClass(body, 'sidebar-light');
      const sidebarDefaultTheme = document.querySelector(
        '#sidebar-default-theme'
      );
      if (sidebarDefaultTheme) {
        this.renderer.addClass(sidebarDefaultTheme, 'selected');
      }
    } else if (theme === 'dark') {
      this.renderer.addClass(body, 'sidebar-dark');
      const sidebarDarkTheme = document.querySelector('#sidebar-dark-theme');
      if (sidebarDarkTheme) {
        this.renderer.addClass(sidebarDarkTheme, 'selected');
      }
    }
  }

  changeNavbarTheme(theme: string) {
    const navbar = document.querySelector('.navbar');
    if (!navbar) return;

    const navbarClasses = [
      'navbar-danger',
      'navbar-success',
      'navbar-warning',
      'navbar-dark',
      'navbar-light',
      'navbar-primary',
      'navbar-info',
      'navbar-pink',
    ];

    navbarClasses.forEach((className) => {
      this.renderer.removeClass(navbar, className);
    });

    document.querySelectorAll('.color-tiles .tiles').forEach((el) => {
      this.renderer.removeClass(el, 'selected');
    });

    const themeClass = theme !== 'default' ? `navbar-${theme}` : '';
    if (themeClass) {
      this.renderer.addClass(navbar, themeClass);
    }

    const tileElement = document.querySelector(`.tiles.${theme}`);
    if (tileElement) {
      this.renderer.addClass(tileElement, 'selected');
    }
  }

  changeLayout(value: string) {
    const layoutClasses = [
      'boxed-layout',
      'sidebar-icon-only',
      'sidebar-mini',
      'sidebar-fixed',
      'sidebar-toggle-display',
      'sidebar-hidden',
      'sidebar-absolute',
    ];

    const body = document.querySelector('body');
    const themeSettings = document.querySelector('#theme-settings');
    if (!body || !themeSettings) return;

    // Remove all layout classes
    layoutClasses.forEach((className) => {
      this.renderer.removeClass(body, className);
    });

    switch (value) {
      case 'boxed':
        this.renderer.addClass(body, 'boxed-layout');
        break;
      case 'icon-menu':
        this.renderer.addClass(body, 'sidebar-icon-only');
        break;
      case 'compact-menu':
        this.renderer.addClass(body, 'sidebar-mini');
        break;
      case 'fixed-sidebar':
        this.renderer.addClass(body, 'sidebar-fixed');
        break;
      case 'hidden-sidebar':
        this.renderer.addClass(body, 'sidebar-toggle-display');
        this.renderer.addClass(body, 'sidebar-hidden');
        break;
      case 'overlay-sidebar':
        this.renderer.addClass(body, 'sidebar-absolute');
        this.renderer.addClass(body, 'sidebar-hidden');
        break;
    }

    this.renderer.removeClass(themeSettings, 'open');
  }
}
